/**
 * Read-only job inspector: status, progress, failure reason, and per-URL payload breakdown.
 *
 * Where: opened from `StreamJobsList` row actions.
 * Uses: `Job` type from `api/job`; MUI `Dialog` + `LinearProgress`.
 * Behavior: closes on action button; tolerates missing payload fields.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import type { Job } from '@/api/job';

interface StreamJobDetailsModalProps {
  open: boolean;
  onClose: () => void;
  job: Job | null;
}

export const StreamJobDetailsModal: React.FC<StreamJobDetailsModalProps> = ({
  open,
  onClose,
  job,
}) => {
  if (!job) return null;

  const childImporterQueueName = 'prospect_pipeline_import_url';
  const isChildImporterJob = job.queue === childImporterQueueName;

  const urls =
    job.payload?.urls ||
    (job.payload?.url ? [job.payload.url] : []);

  const processedUrls = (() => {
    if (!isChildImporterJob) {
      return job.payload?.processed_urls || {};
    }

    // Child importer jobs don't have per-URL processed_urls; infer a single status from the job itself.
    const onlyUrl = urls[0];
    if (!onlyUrl) return {};

    if (job.status === 'DONE') {
      return { [onlyUrl]: 'success' };
    }
    if (job.status === 'FAILED') {
      const message = job.failed_reason ? `error: ${job.failed_reason}` : 'error';
      return { [onlyUrl]: message };
    }
    if (job.status === 'STOP') {
      return { [onlyUrl]: 'processing' };
    }
    // TODO / IN_PROGRESS / WAITING
    return { [onlyUrl]: 'processing' };
  })();
  const progress = job.progress ? Math.round(job.progress * 100) : 0;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DONE':
        return 'success';
      case 'IN_PROGRESS':
        return 'info';
      case 'FAILED':
        return 'error';
      case 'STOP':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusLabelAndError = (rawStatus?: string) => {
    if (!rawStatus) {
      return { label: 'Pending', errorMessage: '' };
    }

    const normalized = rawStatus.toLowerCase();
    if (normalized === 'success') {
      return { label: 'Success', errorMessage: '' };
    }
    if (normalized === 'not_found') {
      return { label: 'Not Found', errorMessage: '' };
    }
    if (normalized === 'processing') {
      return { label: 'Processing', errorMessage: '' };
    }
    if (normalized === 'error') {
      return { label: 'Error', errorMessage: '' };
    }
    if (normalized.startsWith('error')) {
      // Try to extract a human readable message
      const message = rawStatus.replace(/^error[:\s-]*/i, '') || 'Unknown error';
      return { label: 'Error', errorMessage: message };
    }

    return { label: rawStatus, errorMessage: '' };
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="h6">Job Details</Typography>
            <Typography variant="body2" color="text.secondary">
              {job.queue}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Chip label={job.status} color={getStatusColor(job.status) as any} size="small" />
            <Typography variant="body2" color="text.secondary">
              Created: {formatDate(job.created_at)}
            </Typography>
            {job.started_at && (
              <Typography variant="body2" color="text.secondary">
                Started: {formatDate(job.started_at)}
              </Typography>
            )}
            {job.available_at && (
              <Typography variant="body2" color="text.secondary">
                Scheduled: {formatDate(job.available_at)}
              </Typography>
            )}
          </Box>

          {typeof job.progress === 'number' && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Progress</Typography>
                <Typography variant="body2">{progress}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          )}

          {job.failed_reason && (
            <Alert severity="error">
              {job.failed_reason}
            </Alert>
          )}

          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {isChildImporterJob ? 'URL' : 'URL Statuses'}
            </Typography>
            {urls.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No URLs associated with this job.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  maxHeight: 380,
                  overflow: 'auto',
                }}
              >
                {urls.map((url) => {
                  const rawStatus = processedUrls[url];
                  const { label, errorMessage } = getStatusLabelAndError(rawStatus);
                  const isError = !!errorMessage || (rawStatus && rawStatus.toLowerCase().startsWith('error'));

                  return (
                    <Box
                      key={url}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'background.default',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ flex: 1, mr: 2, wordBreak: 'break-all' }}
                      >
                        {url}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          size="small"
                          label={label}
                          color={
                            label === 'Success'
                              ? 'success'
                              : label === 'Processing'
                              ? 'info'
                              : label === 'Not Found'
                              ? 'warning'
                              : isError
                              ? 'error'
                              : 'default'
                          }
                          variant={label === 'Success' ? 'filled' : 'outlined'}
                        />
                        {isError && (
                          <Tooltip title={errorMessage || rawStatus || 'Error'}>
                            <IconButton size="small">
                              <ErrorOutlineIcon color="error" fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};


